import React from 'react';
import styles from './course-dates.module.scss';
import { Term } from '../../../models/courses/term';
import { CourseInfo } from '../../../models/courses/course-info';
import { MessageKeys, useTranslations } from 'next-intl';
import { formatMonthfromDate } from '../../../utils/string-helpers/formatting.helper';

type Props = {
  courseInfos: CourseInfo[];
  isCountryUS: boolean;
  brandTermName: string;
};

const ImportantDatesTermsItem: React.FC<{
  term: Term;
  index: number;
  brandTermName: string;
}> = ({ term, index, brandTermName }) => {
  return (
    <li className='col'>
      {!!term.seasonDescription && (
        <>
          {brandTermName} {index + 1}
          {': '}
        </>
      )}

      <span className={styles['date']}>{term.startDateString}</span>
      {' - '}
      <span className={styles['date']}>{term.endDateString}</span>
    </li>
  );
};

const ImportantDatesTermsItemUS: React.FC<{
  term: Term;
  index: number;
  brandTermName: string;
}> = ({ term, index, brandTermName }) => {
  const translate = useTranslations();

  return (
    <>
      <li className='col'>
        {!!term.seasonDescription && (
          <>
            {brandTermName} {index + 1}{' '}
            {translate(
              term.seasonDescription.translationKey as MessageKeys<any, any>,
            ).toLowerCase()}
            :{' '}
          </>
        )}

        <span className={styles['date']}>{term.startDateString}</span>
        {' - '}
        <span className={styles['date']}>{term.endDateString}</span>
      </li>
    </>
  );
};

const ImportantDatesHeader: React.FC<{
  courseInfo: CourseInfo;
  index: number;
}> = ({ courseInfo, index }) => {
  const firstTerm = courseInfo.terms[0];
  const lastTerm = courseInfo.terms[courseInfo.terms.length - 1];

  return (
    <>
      <h5 className={!index ? styles['year'] : styles['year-with-separator']}>
        Academic Year {courseInfo.academicYear}
      </h5>
      <p className={styles['paragraph']}>
        {firstTerm.startDateString}
        {' - '}
        {lastTerm.endDateString}
      </p>
    </>
  );
};

const ImportantDatesHeaderUS: React.FC<{
  courseInfo: CourseInfo;
  index: number;
}> = ({ courseInfo, index }) => {
  const translate = useTranslations();

  const firstTerm = courseInfo.terms[0];
  const lastTerm = courseInfo.terms[courseInfo.terms.length - 1];

  return (
    <>
      <h5 className={!index ? styles['year'] : styles['year-with-separator']}>
        {firstTerm.startDate.getFullYear()}{' '}
        {translate(firstTerm.seasonDescription.translationKey as MessageKeys<any, any>)}
        {' Start ('}
        {formatMonthfromDate(firstTerm.startDateString, firstTerm.locale)}
        {')'}
      </h5>
      <p className={styles['paragraph']}>
        {translate('Courses.importantDates.courseSynonymUS')}{' '}
        {translate('Courses.importantDates.startDate')} : {firstTerm.startDateString} -{' '}
        {translate('Courses.importantDates.courseSynonymUS')}{' '}
        {translate('Courses.importantDates.endDate')}: {lastTerm.endDateString}
      </p>
    </>
  );
};

export default function CourseDatesPathway({ courseInfos, isCountryUS, brandTermName }: Props) {
  if (!courseInfos.length) {
    return <>{'No term dates'}</>;
  }

  return (
    <div className={styles['important-dates-us-pathway']}>
      {courseInfos?.map((courseInfo, index) => (
        <div key={courseInfo.instanceId}>
          <div className='row mb-3'>
            <div className='col-12'>
              {isCountryUS ? (
                <ImportantDatesHeaderUS courseInfo={courseInfo} index={index} />
              ) : (
                <ImportantDatesHeader courseInfo={courseInfo} index={index} />
              )}

              <ul>
                {courseInfo.terms.map((record, index) => (
                  <>
                    {isCountryUS ? (
                      <ImportantDatesTermsItemUS
                        key={record.id}
                        term={record}
                        index={index}
                        brandTermName={brandTermName}
                      />
                    ) : (
                      <ImportantDatesTermsItem
                        key={record.id}
                        term={record}
                        index={index}
                        brandTermName={brandTermName}
                      />
                    )}
                  </>
                ))}
              </ul>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
